import React, { ReactNode, useContext, useState } from 'react';
// import { useTranslation } from 'react-i18next';

// MUI
import { Box, Typography, Divider, Menu, Paper, Stack, useTheme, Accordion, AccordionSummary, AccordionDetails, Fab } from '@mui/material';

// MUI Icons
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// Custom
import Show3DButtonListItem from './LayerFunctionalities/Show3DButtonListItem';
import AttachDocumentButtonListItem from './LayerFunctionalities/AttachDocumentButtonListItem';
import GoogleMapsListItem from './LayerFunctionalities/GoogleMapsListItem';
import ShowOnGoogleMapsListItem from './LayerFunctionalities/ShowOnGoogleMapsListItem';
import StreetViewListItem from './LayerFunctionalities/StreetViewListItem';
import UndockListItem from './LayerFunctionalities/UndockListItem';
import CommentView from '@/lib/commentViews';
import DocumentViews from '@/lib/documentViews';
import CommentListItem from './LayerFunctionalities/CommentListItem';
import MapCardHeaderID from '@/ui/MapCard/MapCardHeaderID';
import UserContext from '@/context/UserContext/UserContext';
import Permissions from '@/lib/permissions';

// Types
import { GFIFeatureType } from '@/@types/services/gsService';
import EnumLayerFunctionality from '@/@types/services/gsServiceEnums';
import { IGFICardOverlayData } from '@/@types/views/map';
import ShowPhotodocButtonListItem from './LayerFunctionalities/ShowPhotodocButtonListItem';

type SidebarFeatureCardProps = {
  feature: GFIFeatureType;
  index: number;
  isActive: boolean;
  onClick: (index: number) => void;
  // fields: any[];
  children: JSX.Element | JSX.Element[];
  title: string;
  featureData: IGFICardOverlayData;
  FormContent: ReactNode;
  onUndock?: () => void;
};

const SidebarFeatureCard: React.FC<SidebarFeatureCardProps> = ({
  feature,
  index,
  isActive,
  onClick,
  children,
  title,
  FormContent,
  featureData,
  onUndock,
}) => {
  // const { t } = useTranslation();
  const theme = useTheme();

  const [expanded, setExpanded] = useState(index === 0);
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const userContext = useContext(UserContext);

  const { layerData } = featureData;
  const { layerFunctionalityId } = layerData;

  // Prefer numeric id from properties if available
  const displayId = typeof feature.properties?.id === 'number' || /^\d+$/.test(feature.properties?.id) ? feature.properties.id : feature.id;
  const actionsMenuOpen = Boolean(actionsMenuAnchorEl);

  const handlePaperClick = () => {
    onClick(index);
  };

  const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    // Prevent collapsing if not active
    if (!isActive && !isExpanded) return;
    setExpanded(isExpanded);
  };

  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchorEl(event.currentTarget);
  };
  const handleCloseActionsMenu = () => {
    setActionsMenuAnchorEl(null);
  };

  const generateActionList = (): ReactNode[] => {
    const nodes: ReactNode[] = [];

    if (userContext?.hasAnyPermission([Permissions.ThreeDView]) && layerFunctionalityId === EnumLayerFunctionality.classify) {
      nodes.push(
        // Will not render if client does not have permission for 3D
        <Show3DButtonListItem key="show-3d-li" handleCloseMenu={handleCloseActionsMenu} featureData={featureData} />
      );
    }

    if (feature.properties.gid) {
      if (userContext?.hasAnyPermission([Permissions.Private])) {
        nodes.push(
          <AttachDocumentButtonListItem
            stats={{ loading: false, count: 0 }}
            key="attach-document-li"
            handleCloseMenu={handleCloseActionsMenu}
            gid={feature.properties.gid as string}
            onDialogClose={() => {}}
          />,
          <CommentListItem
            stats={{ loading: false, count: 0 }}
            key="comment-li"
            handleCloseMenu={handleCloseActionsMenu}
            gid={feature.properties.gid as string}
            feature={feature}
            onDialogClose={() => {}}
          />
        );
      } else {
        if (userContext?.hasAnyPermission([Permissions.PublicDocuments])) {
          nodes.push(
            <AttachDocumentButtonListItem
              stats={{ loading: false, count: 0 }}
              key="attach-document-li"
              handleCloseMenu={handleCloseActionsMenu}
              gid={feature.properties.gid as string}
              onDialogClose={() => {}}
              view={DocumentViews.View}
            />
          );
        }
        if (userContext?.hasAnyPermission([Permissions.PublicComments])) {
          nodes.push(
            <CommentListItem
              stats={{ loading: false, count: 0 }}
              key="comment-li"
              handleCloseMenu={handleCloseActionsMenu}
              gid={feature.properties.gid as string}
              feature={feature}
              onDialogClose={() => {}}
              view={CommentView.Add}
            />
          );
        }
      }
    }

    switch (layerFunctionalityId) {
      case EnumLayerFunctionality.odvoz_location_button:
        break;
      case EnumLayerFunctionality.kuce_za_odmor_button:
        break;
      case EnumLayerFunctionality.grobna_mjesta_show_ukopani:
        nodes.push(
          <ShowPhotodocButtonListItem key="photodoc-btn" featureData={featureData} type="grobno_mjesto"/>
        );
        break;
      case EnumLayerFunctionality.google_maps_navigate_button:
        nodes.push(<GoogleMapsListItem key="google-maps-li" featureData={featureData} handleCloseMenu={handleCloseActionsMenu} />);
        break;
      default:
        break;
    }

    nodes.push(<ShowOnGoogleMapsListItem key="show-on-google-maps-li" featureData={featureData} handleCloseMenu={handleCloseActionsMenu} />);
    nodes.push(<StreetViewListItem key="street-view-li" featureData={featureData} handleCloseMenu={handleCloseActionsMenu} />);

    if (onUndock) {
      nodes.push(<UndockListItem key="undock-li" handleUndock={onUndock} closeMenu={handleCloseActionsMenu} />);
    }

    return nodes;
  };

  const actionListNodes = generateActionList();

  return (
    <Paper
      elevation={isActive ? 6 : 2}
      sx={{
        border: isActive ? `2px solid ${theme.palette.primary.main}` : '1px solid #ccc',
        cursor: 'pointer',
        width: '100%',
        p: 1,
      }}
      onClick={handlePaperClick}
    >
      <Accordion
        onChange={handleAccordionChange}
        expanded={expanded}
        defaultExpanded={index === 0}
        disableGutters
        sx={{ boxShadow: 'unset' }}
      >
        <AccordionSummary slotProps={{ content: { sx: { m: 0 } }, root: { sx: { p: 0 } } }} expandIcon={<ArrowDownwardIcon />}>
          <Box display="flex" flexDirection="column">
            <Typography variant="body1">{title}</Typography>
            <Stack direction="row" alignItems="center" marginLeft="-4px">
              <MapCardHeaderID id={displayId} color="black" />
              <Typography
                noWrap
                fontSize="0.85rem"
                lineHeight={1.15}
                sx={{ display: { xs: 'none', md: 'block' }, maxWidth: '100%' }}
                variant="body2"
              >
                {displayId}
              </Typography>
            </Stack>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, m: 0 }}>
          <Stack spacing={0.5}>
            <Divider />
            {FormContent}

            {children && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start',
                }}
              >
                {children}
                {actionListNodes.length ? (
                  <>
                    <Fab
                      color="primary"
                      aria-label="more"
                      id="long-button"
                      aria-controls={actionsMenuOpen ? 'long-menu' : undefined}
                      aria-expanded={actionsMenuOpen ? 'true' : undefined}
                      aria-haspopup="true"
                      onClick={handleOpenActionsMenu}
                      size="small"
                      sx={{
                        minWidth: '32px',
                        minHeight: '32px',
                        height: '32px',
                        width: '32px',
                        alignSelf: 'flex-end',
                      }}
                    >
                      <MoreVertIcon fontSize="medium" />
                    </Fab>
                    <Menu
                      id="long-menu"
                      MenuListProps={{
                        'aria-labelledby': 'long-button',
                      }}
                      sx={{ p: 0 }}
                      anchorEl={actionsMenuAnchorEl}
                      open={actionsMenuOpen}
                      onClose={handleCloseActionsMenu}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                      {actionListNodes}
                    </Menu>
                  </>
                ) : null}
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default SidebarFeatureCard;
